import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Calendar as CalendarIcon, Edit, Trash2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useAppointments } from '@/hooks/useAppointments';
import { Badge } from '@/components/ui/badge';
import { format, isPast, isToday } from 'date-fns';
import { DeleteConfirmDialog } from '@/components/DeleteConfirmDialog';
import { useToast } from '@/hooks/use-toast';

export default function AppointmentList() {
  const { petId } = useParams();
  const navigate = useNavigate();
  const { appointments, isLoading, error, deleteAppointmentRecord } = useAppointments(petId as string);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingAppointment, setDeletingAppointment] = useState<{ id: string; type: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { toast } = useToast();

  const handleDeleteAppointment = async () => {
    if (!deletingAppointment) return;
    
    setIsDeleting(true);
    try {
      await deleteAppointmentRecord(deletingAppointment.id);
      toast({
        title: "Appointment deleted",
        description: "Appointment has been successfully deleted.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete appointment. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setDeletingAppointment(null);
    }
  };

  if (isLoading) {
    return <div className="p-4 text-center">Loading appointments...</div>;
  }

  if (error) {
    return (
      <div className="p-4 text-center">
        <p className="text-red-500">Error: {error.message}</p>
        <Button onClick={() => navigate(`/medical/${petId}`)} className="mt-4">
          Back to Medical Records
        </Button>
      </div>
    );
  }

  // Get the status badge for an appointment
  const getStatusBadge = (status: string, appointmentDateTime: string) => {
    if (status === 'completed') {
      return <Badge variant="default" className="bg-green-100 text-green-800">Completed</Badge>;
    }
    if (status === 'cancelled') {
      return <Badge variant="destructive">Cancelled</Badge>;
    }
    
    const appointmentDate = new Date(appointmentDateTime);
    const today = new Date();
    
    if (isPast(appointmentDate) && status !== 'completed') {
      return <Badge variant="destructive">Missed</Badge>;
    }
    
    if (isToday(appointmentDate)) {
      return <Badge variant="default" className="bg-blue-100 text-blue-800">Today</Badge>;
    }
    
    return <Badge variant="secondary">Upcoming</Badge>;
  };

  // Format time from datetime string
  const getAppointmentTime = (dateTimeStr: string) => {
    return format(new Date(dateTimeStr), 'h:mm a');
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Appointments</h1>
        <Button
          onClick={() => navigate(`/medical/${petId}/appointments/new`)}
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Schedule Appointment
        </Button>
      </div>
      
      <div className="grid gap-4">
        {appointments.length === 0 ? (
          <Card>
            <CardContent className="p-6 text-center">
              <p className="text-muted-foreground mb-4">No appointments scheduled.</p>
              <Button onClick={() => navigate(`/medical/${petId}/appointments/new`)}>
                Schedule First Appointment
              </Button>
            </CardContent>
          </Card>
        ) : (
          appointments.map((appointment) => {
            const statusBadge = getStatusBadge(appointment.status, appointment.appointment_datetime);
            const statusColor = appointment.status === 'completed' ? 'bg-green-500' :
                              appointment.status === 'cancelled' ? 'bg-red-500' :
                              isPast(new Date(appointment.appointment_datetime)) ? 'bg-red-500' :
                              isToday(new Date(appointment.appointment_datetime)) ? 'bg-blue-500' : 'bg-gray-500';
            
            return (
              <Card key={appointment.id} className="relative">
                <div className={`absolute top-0 left-0 w-1 h-full ${statusColor} rounded-l-lg`}></div>
                <CardContent className="p-4 pl-6">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="font-semibold text-lg">{appointment.appointment_type}</h3>
                      {statusBadge}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => navigate(`/medical/${petId}/appointments/edit/${appointment.id}`)}
                        className="flex items-center gap-1"
                      >
                        <Edit className="h-3 w-3" />
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => {
                          setDeletingAppointment({ id: appointment.id, type: appointment.appointment_type });
                          setDeleteDialogOpen(true);
                        }}
                        className="flex items-center gap-1"
                      >
                        <Trash2 className="h-3 w-3" />
                        Delete
                      </Button>
                    </div>
                  </div>
                  
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <CalendarIcon className="h-4 w-4" />
                      <span>
                        {format(new Date(appointment.appointment_datetime), 'EEEE, MMMM d, yyyy')} at {getAppointmentTime(appointment.appointment_datetime)}
                      </span>
                    </div>
                    
                    {appointment.vet_name && (
                      <p>Vet: {appointment.vet_name}</p>
                    )}
                    
                    {appointment.location && (
                      <p>Location: {appointment.location}</p>
                    )}
                    
                    {appointment.notes && (
                      <p className="text-foreground">{appointment.notes}</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDeleteAppointment}
        title="Delete Appointment"
        description="Are you sure you want to delete this appointment?"
        itemName={deletingAppointment?.type || ''}
        isLoading={isDeleting}
      />
    </div>
  );
}